import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import useAxiosClient from "../axios-client";

dayjs.extend(relativeTime);

export default function NotificationDropdown({ iconBtn, unseenReleases = [] }) {
  const axiosClient = useAxiosClient();
  const clientRef = useRef(axiosClient);
  clientRef.current = axiosClient;
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // 'all' | 'unread'
  const containerRef = useRef(null);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await clientRef.current.get("/notifications/unread-count");
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // Ignore background poll errors
    }
  }, []);

  // Fetch notification list
  const fetchNotifications = useCallback(async (unreadOnly = false) => {
    setLoading(true);
    try {
      const res = await clientRef.current.get("/notifications", {
        params: {
          unread_only: unreadOnly ? 1 : 0,
          per_page: 25,
        },
      });
      setNotifications(res.data.data || []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll count periodically and on window focus/visibility
  useEffect(() => {
    fetchUnreadCount();

    const handleFocus = () => {
      fetchUnreadCount();
      if (open) {
        fetchNotifications(filter === "unread");
      }
    };

    const handleVisibility = () => {
      if (!document.hidden) {
        handleFocus();
      }
    };

    // Fast polling: 10s normally, 5s when dropdown is open
    const pollInterval = open ? 5000 : 10000;
    const interval = setInterval(() => {
      fetchUnreadCount();
      if (open) {
        fetchNotifications(filter === "unread");
      }
    }, pollInterval);

    window.addEventListener("focus", handleFocus);
    window.addEventListener("orders:status-changed", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("orders:status-changed", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchUnreadCount, fetchNotifications, open, filter]);

  // When dropdown opens or filter changes, reload
  useEffect(() => {
    if (open) {
      fetchNotifications(filter === "unread");
      fetchUnreadCount();
    }
  }, [open, filter, fetchNotifications, fetchUnreadCount]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Handle clicking a notification
  const handleItemClick = async (notif) => {
    const payload = notif.data || {};

    if (!notif.read_at) {
      try {
        await axiosClient.patch(`/notifications/${notif.id}/read`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, read_at: new Date().toISOString() } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // Continue navigation regardless
      }
    }

    setOpen(false);

    if (payload.order_id) {
      navigate(`/orders/${payload.order_id}`);
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await axiosClient.post("/notifications/mark-all-read");
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch {
      // Ignore
    }
  };

  // Render type tag
  const renderTag = (type) => {
    if (type === "ORDER_HELD") {
      return (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
          background: "rgba(245, 158, 11, 0.15)", color: "#f59e0b",
        }}>
          HOLD
        </span>
      );
    }
    if (type === "HOLD_RELEASED") {
      return (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
          background: "rgba(16, 185, 129, 0.15)", color: "#10b981",
        }}>
          RELEASED
        </span>
      );
    }
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
        background: "rgba(59, 130, 246, 0.15)", color: "#3b82f6",
      }}>
        STATUS
      </span>
    );
  };

  return (
    <div style={{ position: "relative" }} ref={containerRef}>
      {/* Bell Trigger */}
      <button
        type="button"
        className="et-icbtn"
        title="Notifications"
        onClick={() => setOpen(!open)}
        style={{
          ...iconBtn,
          position: "relative",
          cursor: "pointer",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9z" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>

        {/* Unread Counter Badge */}
        {unreadCount > 0 ? (
          <span
            style={{
              position: "absolute",
              top: 2,
              right: 1,
              minWidth: 15,
              height: 15,
              padding: "0 3px",
              borderRadius: 8,
              background: "#ef4444",
              color: "#fff",
              fontSize: 9,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 0 1.5px var(--header)",
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : unseenReleases && unseenReleases.length > 0 ? (
          <span
            style={{
              position: "absolute",
              top: 5,
              right: 6,
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#ec1c7e",
              border: "1.5px solid var(--header)",
            }}
          />
        ) : null}
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 6,
            width: 360,
            maxHeight: 460,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            boxShadow: "0 10px 28px rgba(0, 0, 0, 0.22)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            fontFamily: "Outfit, system-ui, sans-serif",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "var(--surface2)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: "1px 6px", borderRadius: 10,
                  background: "var(--activeBg)", color: "var(--primary)",
                }}>
                  {unreadCount} new
                </span>
              )}
            </div>

            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                style={{
                  border: "none", background: "transparent", color: "var(--muted)",
                  fontSize: 11, cursor: "pointer", textDecoration: "underline", padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Filter Bar */}
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid var(--border)",
              background: "var(--surface)",
              fontSize: 12,
            }}
          >
            <button
              type="button"
              onClick={() => setFilter("all")}
              style={{
                flex: 1, padding: "7px 0", border: "none", background: "transparent",
                cursor: "pointer", fontWeight: filter === "all" ? 700 : 500,
                color: filter === "all" ? "var(--text)" : "var(--dim)",
                borderBottom: filter === "all" ? "2px solid var(--primary)" : "none",
              }}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setFilter("unread")}
              style={{
                flex: 1, padding: "7px 0", border: "none", background: "transparent",
                cursor: "pointer", fontWeight: filter === "unread" ? 700 : 500,
                color: filter === "unread" ? "var(--text)" : "var(--dim)",
                borderBottom: filter === "unread" ? "2px solid var(--primary)" : "none",
              }}
            >
              Unread {unreadCount > 0 ? `(${unreadCount})` : ""}
            </button>
          </div>

          {/* Notification Items List */}
          <div style={{ overflowY: "auto", flex: 1, maxHeight: 340 }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "var(--dim)" }}>
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: 32, textAlign: "center" }}>
                <div style={{ fontSize: 24, marginBottom: 6, opacity: 0.5 }}>🔔</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
                  No notifications
                </div>
                <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 2 }}>
                  {filter === "unread" ? "You're all caught up!" : "Status updates will show here."}
                </div>
              </div>
            ) : (
              notifications.map((item) => {
                const data = item.data || {};
                const isUnread = !item.read_at;
                const orderRef = data.order_id ? `#${data.order_id}` : '';
                const displayOrderNumber = orderRef || (data.order_number && !data.order_number.includes('ET-ORD-') ? data.order_number : '');
                const displayMessage = (data.message || '')
                  .replace(/(?:Order\s+)?ET-ORD-[0-9a-zA-Z_-]+/gi, orderRef ? `Order ${orderRef}` : 'Order')
                  .replace(/Order\s+Order\s+/gi, 'Order ');
                const displayHoldReason = data.hold_reason
                  ? data.hold_reason.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
                  : null;

                return (
                  <div
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    style={{
                      padding: "10px 14px",
                      borderBottom: "1px solid var(--border)",
                      cursor: "pointer",
                      background: isUnread ? "rgba(59, 130, 246, 0.05)" : "transparent",
                      transition: "background 0.15s ease",
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = isUnread ? "rgba(59, 130, 246, 0.1)" : "var(--surface2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isUnread ? "rgba(59, 130, 246, 0.05)" : "transparent";
                    }}
                  >
                    {/* Unread indicator dot */}
                    <div style={{ paddingTop: 4, width: 8 }}>
                      {isUnread && (
                        <div
                          style={{
                            width: 7, height: 7, borderRadius: "50%",
                            background: "var(--primary)",
                          }}
                        />
                      )}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 3 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {renderTag(data.notification_type)}
                          {displayOrderNumber && (
                            <span style={{
                              fontSize: 12, fontWeight: 700, color: "var(--text)",
                              fontFamily: "'JetBrains Mono', monospace",
                            }}>
                              {displayOrderNumber}
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: 10, color: "var(--dim)", whiteSpace: "nowrap" }}>
                          {dayjs(item.created_at).fromNow()}
                        </span>
                      </div>

                      <div style={{
                        fontSize: 12, color: "var(--text)", lineHeight: 1.35,
                        wordBreak: "break-word",
                      }}>
                        {displayMessage}
                      </div>

                      {displayHoldReason && (
                        <div style={{
                          marginTop: 4, fontSize: 11, color: "var(--muted)", fontStyle: "italic",
                        }}>
                          Reason: {displayHoldReason}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
